import React from 'react';
import {useTranslation} from 'react-i18next';
import cn from 'classnames';
import {NavLink, useLocation} from 'react-router-dom';
import {LocationStates} from '../../../views/VerifyModalScreen/VerifyModalScreen';
import {GlobalLocationTypes} from '../../../utils/types';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {PATHS} from '../../../Routes';
import MobileNavigation from '../MobileNavigation';
import styles from './CheckinNavigation.module.sass';

export type LinkItem = {
  route: string;
  label: string;
  disabled?: boolean;
};

export type CheckinNavigationProps = {
  className?: string;
  title?: string;
};

const defaultProps: CheckinNavigationProps = {
  title: '',
};

function CheckinNavigation({className, title}: CheckinNavigationProps) {
  const location = useLocation<GlobalLocationTypes>();
  const {t} = useTranslation();
  const {
    hasTaxes,
    hasDeposits,
    hasUpselling,
    isSomePayments,
    hasGuestMembers,
    hasAnyMandatoryPayment,
    hasBiomatchForAllGuests,
    hasBiomatchForGuestLeader,
    hasBiomatchRequiredCurrentGuest,
    hasScanDocumentRequiredCurrentGuest,
  } = useComputedReservationDetails();
  const identityVerificationState =
    location.state?.[LocationStates.identityVerificationState];

  const links = React.useMemo<LinkItem[]>(() => {
    const links = [];
    const hasRequiredPayment = hasGuestMembers && hasAnyMandatoryPayment;
    const needDisablePersonalForm =
      ((hasBiomatchRequiredCurrentGuest || hasScanDocumentRequiredCurrentGuest) &&
        !identityVerificationState?.isIdentityVerificationFinished) ||
      hasRequiredPayment;

    const confirmBookingLink = {
      route: '/form/book',
      label: `${t('confirm')}`,
    };
    links.push(confirmBookingLink);

    if (hasBiomatchForGuestLeader || hasBiomatchForAllGuests) {
      links.push({
        route: '/verification',
        label: `${t('verify')}`,
        disabled: hasRequiredPayment,
      });
    }

    const addPersonalDataLInk = {
      route: '/form/add',
      label: `${t('details')}`,
      disabled: needDisablePersonalForm,
    };
    links.push(addPersonalDataLInk);

    if (hasDeposits) {
      links.push({
        route: PATHS.protect,
        label: `${t('protect')}`,
      });
    }

    if (hasTaxes) {
      links.push({
        route: PATHS.touristTaxes,
        label: t('taxes'),
      });
    }

    if (isSomePayments || hasTaxes) {
      links.push({
        route: PATHS.pay,
        label: t('pay'),
      });
    }

    const finalLink = {
      route: PATHS.finish,
      label: `${t('summary')}`,
      disabled: !hasGuestMembers,
    };
    links.push(finalLink);

    return links;
  }, [
    identityVerificationState?.isIdentityVerificationFinished,
    hasScanDocumentRequiredCurrentGuest,
    hasBiomatchRequiredCurrentGuest,
    hasBiomatchForGuestLeader,
    hasBiomatchForAllGuests,
    hasAnyMandatoryPayment,
    hasGuestMembers,
    isSomePayments,
    hasDeposits,
    hasTaxes,
    t,
  ]);

  const extraLinks = React.useMemo<LinkItem[]>(() => {
    const extraLinks = [];

    if (hasUpselling) {
      extraLinks.push({
        route: PATHS.experiences,
        label: `${t('experiences')}`,
      });
    }

    return extraLinks;
  }, [hasUpselling, t]);

  return (
    <>
      <div className={styles.wrapperNavigation}>
        <div className={cn(className, styles.container)}>
          <div className={styles.title}>{title}</div>
          <div className={styles.links}>
            {links.map(({route, label, disabled}, i) => {
              if (disabled) {
                return (
                  <div
                    key={i}
                    className={cn('crumbsWrapper', styles.wrapperLink, styles.disabled)}
                  >
                    <span className={styles.link}>{label}</span>
                  </div>
                );
              }
              return (
                <NavLink
                  key={i}
                  className={cn('crumbsWrapper', styles.wrapperLink)}
                  to={{
                    pathname: route,
                    state: location.state,
                  }}
                  activeClassName={styles.active}
                >
                  <span className={styles.link}>{label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.wrapperMobileNavigation}>
        <MobileNavigation title={title} links={links} extraLinks={extraLinks} />
      </div>
    </>
  );
}

CheckinNavigation.defaultProps = defaultProps;

export {CheckinNavigation};
